<template>
  <div class="c-integration-form container !pt-0">
    <el-form
      ref="integrationForm"
      :model="integrationForm"
      :rules="formRules"
      label-position="top"
      label-width="120px"
      @submit.native.prevent>

      <!-- Integration Name -->
      <el-row class="mb-4" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Integration Name" prop="name">
            <el-input
              v-model="integrationForm.name"
              class="gray"
              maxlength="255"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Webhook URL -->
      <el-row class="mb-2" :gutter="20">
        <el-col :span="24">
          <el-form-item label="Webhook URL" prop="webhook_integration.webhook_url">
            <el-input v-model="integrationForm.webhook_integration.webhook_url" class="gray"></el-input>
          </el-form-item>
        </el-col>
      </el-row>

      <!-- Field Mapping -->
      <!-- ... (Field mapping code remains the same) ... -->

      <div class="mt-8 flex justify-end">
        <button class="btn secondary outline big mr-4" @click="cancel">
          Cancel
        </button>
        <button class="btn primary big" @click="saveChanges">Done</button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { errorMessages } from '@meta/errorMessages'
import validationMixin from '@components/mixins/validation'

export default {
  name: 'WebhookIntegrationForm',
  mixins: [validationMixin],
  data() {
    const checkValidWebhookURL = (rule, value, callback) => {
      const url = this.integrationForm.webhook_integration.webhook_url
      this.integrationForm.webhook_integration.webhook_url = this.setHttp(url)
      if (!url) {
        callback(new Error('Webhook URL is required'))
      } else {
        callback(this.validateURL(rule, this.integrationForm.webhook_integration.webhook_url, callback))
      }
    }

    return {
      enableUpdate: false,
      errorMessages,
      integrationForm: {
        name: '',
        mapping: [],
        target: 'WEBHOOK',
        webhook_integration: {
          webhook_url: '',
        },
      },
      formRules: {
        name: [
          { required: true, message: 'Integration name is required', trigger: 'blur' },
          { max: 255, message: 'Integration name must be less than 255 characters', trigger: 'blur' },
        ],
        'webhook_integration.webhook_url': [
          { required: true, message: 'Webhook URL is required', trigger: 'blur' },
          { validator: checkValidWebhookURL, trigger: 'blur' },
        ],
      },
      allFieldsActive: false,
      formModified: false,
    }
  },
  computed: {
    ...mapState('integration', ['targetIntegrationID', 'integrations', 'integrationActionLoading']),
    ...mapGetters('campaign/widget', ['getWidgetFields']),
    integration() {
      return this.$store.getters['integration/targetIntegration']
    },
    targetWidgetID() {
      return this.$store.state.campaign.widget.targetWidgetID
    },
  },
  watch: {
    integrationForm: {
      handler() {
        if (this.enableUpdate) {
          this.formModified = true
          this.$emit('update', this.integrationForm)
        }
      },
      deep: true,
      immediate: false,
    },
    formModified(newValue) {
      this.$emit('form-modified', newValue)
    },
  },
  created() {
    this.setFormValues(this.integration)
    this.$nextTick(() => {
      this.enableUpdate = true
    })
  },
  methods: {
    saveChanges() {
      const saveAction = this.targetIntegrationID
          ? 'integration/updateIntegration'
          : 'integration/createIntegration'

      // Update the webhook URL
      this.integrationForm.webhook_integration.webhook_url = this.setHttp(
          this.integrationForm.webhook_integration.webhook_url)

      this.$refs.integrationForm.validate((valid) => {
        if (!valid) return false

        this.$store.dispatch(saveAction, this.integrationForm)
            .then(() => {
              this.$message({ type: 'success', message: 'Save successful!' })
              this.$emit('force-close')
            })
            .catch((error) => {
              this.$message({ type: 'error', message: this.errorMessages.default })
              console.error(error)
            })
      })
    },
    setFormValues(integration) {
      if (integration) {
        this.integrationForm.name = integration.name || ''
        this.integrationForm.mapping = JSON.parse(JSON.stringify(integration.mapping)) || []
        // Set webhook_integration values
        if (integration.webhook_integration) {
          this.integrationForm.webhook_integration.webhook_url = integration.webhook_integration.webhook_url || ''
        }
      }

      this.integrationForm.widget = this.targetWidgetID
      this.syncWidgetFieldsWithMapping()
    },
    syncWidgetFieldsWithMapping() {
      const widgetFields = this.getWidgetFields
      this.integrationForm.mapping = this.integrationForm.mapping.map(mappingItem => {
        const matchingField = widgetFields.find(field => field.id === mappingItem.internal.fieldID)
        if (matchingField) {
          mappingItem.internal.fieldID = matchingField.id
        }
        return mappingItem
      }).filter(mappingItem => widgetFields.some(field => field.id === mappingItem.internal.fieldID))

      widgetFields.forEach(field => {
        if (!this.integrationForm.mapping.some(mappingItem => mappingItem.internal.fieldID === field.id)) {
          this.integrationForm.mapping.push({
            internal: { name: field.settings.label, fieldID: field.id },
            external: { name: field.settings.label },
            active: false,
          })
        }
      })
      this.checkAllFieldsActive()
    },
    setAllFieldsActive(active) {
      this.integrationForm.mapping.forEach(item => {
        item.active = active
      })
    },
    checkAllFieldsActive() {
      this.allFieldsActive = this.integrationForm.mapping.every(item => item.active === true)
    },
    cancel() {
      if (this.formModified) {
        this.$emit('close-drawer')
      } else {
        this.$emit('force-close')
      }
    },
    setHttp(value) {
      if (!/^https?:\/\//i.test(value)) {
        return 'http://' + value
      }
      return value
    },
    validateURL(rule, value, callback) {
      const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-]*)*\/?$/
      if (urlPattern.test(value)) {
        callback()
      } else {
        callback(new Error('Please enter a valid URL'))
      }
    },
  },
}
</script>

<style lang="scss">
.c-integration-form {
  .el-form .el-form-item {
    label {
      @apply font-medium;
    }
  }
}
</style>